import { defineStore } from 'pinia';
import { useStoreModalDepositStatus } from '~/stores/strapi/modals/depositStatus';
import { useStorePostRegistration } from '~/stores/strapi/postRegistration';
import { useStoreStag } from '../client/stag';
import { useStoreSsPlayer } from '../softswiss/player';
import { useStoreAutochangeData } from '../strapi/autochangeData';
import { useStoreBottomMenu } from '../strapi/bottomMenu';
import { useStoreButtonTitles } from '../strapi/buttonTitles';
import { useStoreCataloguePage } from '../strapi/cataloguePage';
import { useStoreCategoryPage } from '../strapi/categoryPage';
import { useStoreChatOptions } from '../strapi/chatOptions';
import { useStoreComponents } from '../strapi/components';
import { useStoreCrossSalePopup } from '../strapi/crossSalePopup';
import { useStoreCryptoCountries } from '../strapi/cryptoCountries';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { useStoreFooter } from '../strapi/footer';
import { useStoreGamePage } from '../strapi/gamePage';
import { useStoreHeader } from '../strapi/header';
import { useStoreLanguages } from '../strapi/languages';
import { useStoreMainpageBanners } from '../strapi/mainpageBanners';
import { useStoreMainpageMenus } from '../strapi/mainpageMenus';
import { useStoreManagers } from '../strapi/managers';
import { useStoreModalAuth } from '../strapi/modals/auth';
import { useStoreBuyCryptoModal } from '../strapi/modals/buyCrypto';
import { useStoreModalCashier } from '../strapi/modals/cashier';
import { useStoreModalCompoints } from '../strapi/modals/compoints';
import { useStoreModalDeposit } from '../strapi/modals/deposit';
import { useStoreModalDepositCurrencies } from '../strapi/modals/depositCurrencies';
import { useStoreModalDepositFiat } from '../strapi/modals/depositFiat';
import { useStoreModalEstonian } from '../strapi/modals/estonianModal';
import { useStoreModalProfile } from '../strapi/modals/profile';
import { useStoreModalTransactionHistory } from '../strapi/modals/transactionHistory';
import { useStoreWithdrawModal } from '../strapi/modals/withdraw';
import { useStoreNotificationsGroup } from '../strapi/notificationsGroup';
import { useStoreNotificationsSettings } from '../strapi/notificationsSettings';
import { useStoreNotificationsWelcome } from '../strapi/notificationsWelcome';
import { useStorePaymentSystemImages } from '../strapi/paymentSystemImages';
import { useStorePaymentSystemSorting } from '../strapi/paymentSystemSorting';
import { useStoreProviders } from '../strapi/providers';
import { useStoreSlotTheme } from '../strapi/slotThemes';
import { useStoreSnippet } from '../strapi/snippet';
import { useStoreSystemHosts } from '../strapi/systemHosts';
import { useStoreVipLevelData } from '../strapi/vipLevelData';

export interface PagesLoadOptions {
  reset?: boolean;
  gameId?: string;
  gameUrl?: string;
}

export const useStorePages = defineStore('abstractPages', {
  state: () => ({
    genarationDate: 0,
    genarationDateMain: 0,
  }),
  actions: {
    /**
     * mutation for saving last request date of generation
     */
    SET_GENERATION_DATE() {
      if (!this.genarationDate) {
        this.genarationDate = Date.now();
      }
    },
    SET_MAIN_GENERATION_DATE() {
      if (!this.genarationDateMain) {
        this.genarationDateMain = Date.now();
      }
    },
    formatCommonDataRequests() {
      return [
        useStoreHeader().loadHeader(),
        useStoreAutochangeData().loadAutochangeData(),
        useStoreProviders().loadProviders(),
        useStoreCurrencyMenu().loadCurrencyMenu(),
        useStoreButtonTitles().loadButtonTitles(),
        useStoreChatOptions().loadChatOptions(),
        useStoreComponents().loadComponents(),
        useStoreCryptoCountries().loadCryptoCountries(),
        useStoreLanguages().loadLanguages(),
        useStoreMainpageMenus().loadMainPageMenu(),
        useStoreSnippet().loadSnippets(),
        useStoreSystemHosts().loadSystemHosts(),
        useStorePaymentSystemSorting().loadPaymentSystemSortingData(),
        useStorePaymentSystemImages().loadPaymentSystemImages(),
        useStoreVipLevelData().loadVipData(),
        useStoreBottomMenu().loadBottomMenu(),
        this.loadModalsData(),
        useStoreCrossSalePopup().loadCrossSalePopups(),
      ];
    },

    async loadMainServerData(): Promise<boolean> {
      try {
        await Promise.all([
          ...(this.genarationDate ? [] : this.formatCommonDataRequests()),
          useStoreSsPlayer().isWithFbFlowSignup &&
            useStorePostRegistration().loadPostRegistration(),
          useStoreFooter().loadFooter(),
          useStoreMainpageBanners().loadMainPageBanners(),
          useStoreCategoryPage().loadCategories(),
        ]);

        this.SET_GENERATION_DATE();
        this.SET_MAIN_GENERATION_DATE();

        return true;
      } catch {
        return false;
      }
    },

    async loadGameServerData(): Promise<boolean> {
      try {
        await Promise.all([
          ...(this.genarationDate ? [] : this.formatCommonDataRequests()),
          useStoreSsPlayer().isWithFbFlowSignup &&
            useStorePostRegistration().loadPostRegistration(),
          useStoreGamePage().loadGamePage(),
          useStoreCategoryPage().loadCategories(),
        ]);

        this.SET_GENERATION_DATE();

        return true;
      } catch {
        return false;
      }
    },

    async loadCatalogueData(force = false): Promise<boolean> {
      if (!force && useStoreCataloguePage().createdAt) {
        return true;
      }

      try {
        await Promise.all([
          useStoreCataloguePage().loadCataloguePage(),
          useStoreCategoryPage().loadCategories(),
          useStoreSlotTheme().loadSlotThemes(),
          useStoreProviders().loadProviders(),
        ]);

        if (import.meta.browser && window) {
          useStoreStag().checkPureStagByWhichWasRegistered();
        }

        return true;
      } catch {
        return false;
      }
    },

    async loadNotifications(): Promise<boolean> {
      try {
        await useStoreManagers().loadManagers();

        await Promise.all([
          useStoreNotificationsSettings().loadNotificationSettings(),
          useStoreNotificationsGroup().loadNotifications(),
          useStoreNotificationsWelcome().loadNotifications(),
        ]);

        if (import.meta.browser && window) {
          useStoreStag().checkPureStagByWhichWasRegistered();
        }

        return true;
      } catch {
        return false;
      }
    },

    async loadModalsData() {
      try {
        await Promise.all([
          useStoreModalProfile().loadProfileModal(),
          useStoreModalDeposit().loadDepositModal(),
          useStoreModalDepositStatus().loadDepositStatusModal(),
          useStoreModalDepositFiat().loadDepositFiatModal(),
          useStoreModalCompoints().loadCompointsModal(),
          useStoreModalTransactionHistory().loadHistoryModal(),
          useStoreModalCashier().loadCashierModal(),
          useStoreModalEstonian().loadEstonianModal(),
          useStoreWithdrawModal().loadWithdrawModal(),
          useStoreBuyCryptoModal().loadBuyCryptoModal(),
          useStoreModalDepositCurrencies().loadDepositCurrenciesModal(),
          useStoreModalAuth().loadAuthModal(),
        ]);

        return true;
      } catch {
        return false;
      }
    },
  },
});
